import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isEqual } from 'lodash';

import { RepeatingAssumption } from '@futrli-sdk/consolidations';
import { AnyObject, useCrudActivityContext } from '@futrli/shared-services';

import {
  RepeatingMethod,
  getInitialRepeatingState,
  initializePredictionDetailsState
} from '../../../components/repeating/repeating-method';
import { AllowedAssumptionTypes } from '../../../../detail/reports/actions/actions';
import { ASSUMPTION_TYPES, TRACKING_EVENTS } from '../../../../../core/constants';
import { PaymentTreatment, PredictionType } from '../../../types';
import { PredictionEditor } from '../prediction-editor/prediction-editor';
import { PredictionHeader } from '../prediction-header/prediction-header';
import { RepeatingMethodState } from '../../../components/repeating/types';
import { trackEvent } from '../../../../../core/tracker';
import { useConsolidatedOrganisationPredictionBuilderContext } from '../../../context/consolidated-organisation-prediction-builder-context';

interface Props {
  onClose?: () => void;
  isPredictionTypeActive?: boolean;
}

export const RepeatingPrediction = ({ onClose, isPredictionTypeActive }: Props) => {
  const {
    accountIndex,
    currency,
    handleSaveExisting,
    handleSaveNew,
    isCashFlowDisabled,
    isEditing,
    isQuickCopy,
    paymentTreatment,
    predictionData,
    predictionName,
    predictionType,
    reloadForecastData,
    reset,
    resetUrlData,
    setImpactPreviewParams,
    setIsSaveDisabled,
    setPredictionContentTouched,
    urlPredictionId,
    vatPercentage,
    previewImpactsResponse
  } = useConsolidatedOrganisationPredictionBuilderContext();

  const { addCreating, removeCreating } = useCrudActivityContext();

  const [data, setData] = useState<RepeatingMethodState>(getInitialRepeatingState());
  const [validData, setValidData] = useState<RepeatingMethodState>();
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isDataValid, setIsDataValid] = useState(false);

  const isActive = predictionType === PredictionType.REPEATING;
  const isFirstRun = useRef(true);
  const lastImpactPreviewParams = useRef<Partial<AllowedAssumptionTypes>>();

  // *********
  // * Utils *
  // *********

  const getSaveParams = useCallback(() => {
    const fixedData = (data as unknown) as Partial<RepeatingAssumption>;
    const params: Partial<RepeatingAssumption> = {
      ...fixedData,
      name: predictionName,
      vat_percentage: paymentTreatment === PaymentTreatment.NON_CASH_TRANSFER ? '0' : vatPercentage || null,
      account_id: accountIndex,
      excluded_from_report: false
    };
    return params;
  }, [accountIndex, data, paymentTreatment, predictionName, vatPercentage]);

  const refreshAccountsImpactPreview = useCallback(async () => {
    if (accountIndex && !isCashFlowDisabled && validData) {
      const newParams = {
        ...getSaveParams(),
        name: 'repeating-impact-preview'
      };
      if (!isEqual(newParams, lastImpactPreviewParams.current)) {
        lastImpactPreviewParams.current = newParams;
        setImpactPreviewParams(newParams);
      }
    }
  }, [accountIndex, isCashFlowDisabled, validData, getSaveParams, setImpactPreviewParams]);

  const restoreInitialContent = useCallback(() => {
    setData(getInitialRepeatingState());
    setIsDataValid(false);
    return true;
  }, []);

  const restoreLoadedContent = useCallback(() => {
    if (predictionData) {
      setData(initializePredictionDetailsState(predictionData.assumption_data as RepeatingAssumption));
      return true;
    }
    return false;
  }, [predictionData]);

  const save = useCallback(async () => {
    const params = getSaveParams();

    let success = false;
    if (urlPredictionId && data && !isQuickCopy) {
      success = await handleSaveExisting(params);
    } else {
      success = !!(await handleSaveNew(params));
      if (success) {
        trackEvent(TRACKING_EVENTS.CREATED_ANY_MANUAL_PREDICTION);
        trackEvent(TRACKING_EVENTS.ADD_PREDICTION_FORMULA_CONSOLIDATION);
      }
    }
    return success;
  }, [data, getSaveParams, handleSaveExisting, handleSaveNew, isQuickCopy, urlPredictionId]);

  // ************
  // * Handlers *
  // ************

  const handleContentChange = useCallback(
    (isValid: boolean, data: AnyObject) => {
      setPredictionContentTouched(true);
      setIsFormSubmitted(false);
      const newRepeatingData = (data as unknown) as RepeatingMethodState;
      setIsDataValid(isValid);
      setData(newRepeatingData);
      if (isValid) {
        setValidData(newRepeatingData);
      }
    },
    [setPredictionContentTouched]
  );

  const handleSave = useCallback(async () => {
    addCreating('repeating-prediction');
    const success = await save();
    if (success) {
      reloadForecastData();
      onClose?.();
    }
    removeCreating('repeating-prediction');
  }, [addCreating, onClose, reloadForecastData, removeCreating, save]);

  const handleSaveAndNew = useCallback(async () => {
    addCreating('repeating-prediction');
    const success = await save();
    if (success) {
      reloadForecastData();
      reset();
      resetUrlData();
      restoreInitialContent();
    }
    removeCreating('repeating-prediction');
  }, [addCreating, reloadForecastData, removeCreating, reset, resetUrlData, restoreInitialContent, save]);

  // ***********
  // * Effects *
  // ***********

  // Set initial / restore saved content on first run
  useEffect(() => {
    if (isFirstRun.current) {
      let contentWasRestored = false;

      if (isEditing || isQuickCopy) {
        if (predictionData) {
          if (predictionData.assumption_type === ASSUMPTION_TYPES.REPEATING) {
            contentWasRestored = restoreLoadedContent();
          } else {
            contentWasRestored = restoreInitialContent();
          }
        }
      } else {
        contentWasRestored = restoreInitialContent();
      }

      isFirstRun.current = !contentWasRestored;
    }
  }, [isEditing, predictionData, restoreInitialContent, restoreLoadedContent, isQuickCopy]);

  // Refresh the accounts impacted info
  useEffect(() => {
    if (isActive) {
      refreshAccountsImpactPreview();
    } else {
      lastImpactPreviewParams.current = undefined;
    }
  }, [isActive, refreshAccountsImpactPreview]);

  // Update isSaveDisabled flag
  useEffect(() => {
    if (isActive) {
      setIsSaveDisabled(!isDataValid);
    }
  }, [isActive, isDataValid, setIsSaveDisabled]);

  return (
    <PredictionEditor
      onSave={handleSave}
      onSaveAndNew={handleSaveAndNew}
      show={isActive}
      onClose={onClose}
      isPredictionTypeActive={isPredictionTypeActive}
    >
      <RepeatingMethod
        currency={currency!}
        PredictionHeader={PredictionHeader}
        onChange={handleContentChange}
        repeatingMethodData={data}
        isFormSubmitted={isFormSubmitted}
        previewImpacts={previewImpactsResponse}
      />
    </PredictionEditor>
  );
};
